import { cloneElement, forwardRef } from "react";
import { Link, useSearchParams } from "react-router-dom";

import cx from "@hsl/core/utils/cx";

export type Props = {
    children?: React.ReactNode;
    color?: "primary" | "white" | "transparent";
    variant?: "default" | "large";
    className?: string;
    disabled?: boolean;
    icon?: React.ReactElement<{ className?: string; "aria-hidden"?: boolean }>;
    text?: React.ReactNode;
} & (
    | {
          as: "link";
          to: string;
          replace?: boolean;
          stripSearchParams?: Array<string>;
          onClick?: React.MouseEventHandler<HTMLAnchorElement>;
      }
    | {
          as?: "button";
          form?: string;
          onClick?: React.MouseEventHandler<HTMLButtonElement>;
          type?: "button" | "submit" | "reset";
      }
);

const Button = forwardRef<HTMLButtonElement | HTMLLinkElement, Props>(
    (props: Props, ref) => {
        const {
            color = "primary",
            className,
            disabled,
            variant = "default",
        } = props;
        const classes = cx(
            "inline-flex items-center justify-center",
            "rounded-sm shadow-sm",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",

            {
                "bg-core-blue hover:bg-core-blue/90 focus-visible:outline-core-blue text-white":
                    color === "primary",
                "border-charcoal text-charcoal focus-visible:outline-charcoal border bg-white hover:bg-white/90 disabled:border-transparent":
                    color === "white",
                "border-charcoal text-charcoal focus-visible:outline-charcoal border bg-transparent hover:bg-white/30 disabled:border-transparent":
                    color === "transparent",
                "pointer-events-none bg-gray-300 text-gray-600 opacity-50":
                    disabled,
                "text-sm font-semibold px-2.5 py-1.5": variant === "default",
                "text-base px-6 py-2.5": variant === "large",
            },
            className,
        );

        switch (props.as) {
            case "link": {
                return <LinkButton {...props} className={classes} />;
            }
            case "button":
            default: {
                const { children, icon, onClick, text, form, type } = props;
                return (
                    <button
                        className={classes}
                        disabled={disabled}
                        onClick={onClick}
                        ref={ref as React.ForwardedRef<HTMLButtonElement>}
                        form={form}
                        type={type}
                    >
                        {!!icon &&
                            cloneElement(icon, {
                                className: cx(
                                    "-ml-0.5 h-5 w-5 mr-2",
                                    icon.props.className,
                                ),
                                "aria-hidden": true,
                            })}
                        {children || text}
                    </button>
                );
            }
        }
    },
);

export default Button;

const LinkButton = forwardRef<HTMLLinkElement, Extract<Props, { as: "link" }>>(
    (
        {
            children,
            icon,
            to,
            replace,
            text,
            onClick,
            stripSearchParams,
            className,
        },
        ref,
    ) => {
        const [searchParams] = useSearchParams();
        const search = new URLSearchParams(searchParams);
        for (const param of stripSearchParams || []) {
            search.delete(param);
        }

        return (
            <Link
                className={className}
                to={to + (search ? `?${search.toString()}` : "")}
                replace={replace}
                ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                onClick={onClick}
            >
                {!!icon &&
                    cloneElement(icon, {
                        className: cx(
                            "-ml-0.5 h-5 w-5 mr-2",
                            icon.props.className,
                        ),
                        "aria-hidden": true,
                    })}
                {children || text}
            </Link>
        );
    },
);
